/**
 * 视觉退让控制器
 * 管理拖拽时的视觉效果，仅对选中碎片做轻微突出
 */
import * as PIXI from 'pixi.js';

export interface DimConfig {
    scaleUp: number;        // 选中时放大比例（默认 1.02，即 2%）
}

export class VisualDimmer {
    private config: DimConfig;

    // 当前选中的碎片 ID
    private currentPieceId: string | null = null;

    // 原始缩放值（用于恢复）
    private originalScale: { x: number; y: number } | null = null;

    // 是否正在退让状态
    private _isActive: boolean = false;

    constructor(config?: Partial<DimConfig>) {
        this.config = {
            scaleUp: 1.02,
            ...config
        };
    }

    /**
     * 开始视觉退让
     * @param currentPieceId 当前拖动的碎片 ID
     * @param _sameRegionIds 同区域的碎片 ID 列表（保留参数兼容性，不再使用）
     * @param allSprites 所有碎片的精灵映射
     */
    startDim(
        currentPieceId: string,
        _sameRegionIds: string[],
        allSprites: Map<string, PIXI.Sprite>
    ): void {
        if (this._isActive) return;
        this._isActive = true;
        this.currentPieceId = currentPieceId;

        // 略微放大当前拖动的碎片
        const currentSprite = allSprites.get(currentPieceId);
        if (currentSprite) {
            // 保存原始缩放
            this.originalScale = {
                x: currentSprite.scale.x,
                y: currentSprite.scale.y
            };
            // 应用放大
            currentSprite.scale.set(
                this.originalScale.x * this.config.scaleUp,
                this.originalScale.y * this.config.scaleUp
            );
        }
    }

    /**
     * 结束视觉退让，恢复碎片的正常显示
     * @param currentPieceId 当前拖动的碎片 ID（用于恢复缩放）
     * @param allSprites 所有碎片的精灵映射
     */
    endDim(currentPieceId: string, allSprites: Map<string, PIXI.Sprite>): void {
        if (!this._isActive) return;
        this._isActive = false;

        // 恢复当前碎片的缩放
        const currentSprite = allSprites.get(currentPieceId);
        if (currentSprite && this.originalScale) {
            currentSprite.scale.set(
                this.originalScale.x,
                this.originalScale.y
            );
        }

        this.currentPieceId = null;
        this.originalScale = null;
    }

    /**
     * 检查是否正在退让状态
     */
    get isActive(): boolean {
        return this._isActive;
    }

    /**
     * 更新配置
     */
    updateConfig(config: Partial<DimConfig>): void {
        this.config = { ...this.config, ...config };
    }

    /**
     * 获取配置
     */
    getConfig(): DimConfig {
        return { ...this.config };
    }

    /**
     * 强制结束退让状态（用于异常情况）
     */
    forceEnd(allSprites: Map<string, PIXI.Sprite>): void {
        if (this.currentPieceId && this.originalScale) {
            const sprite = allSprites.get(this.currentPieceId);
            if (sprite) {
                sprite.scale.set(this.originalScale.x, this.originalScale.y);
            }
        }

        this._isActive = false;
        this.currentPieceId = null;
        this.originalScale = null;
    }

    /**
     * 销毁
     */
    destroy(): void {
        this.currentPieceId = null;
        this.originalScale = null;
    }
}
